import { useRef, useCallback, useState } from "react";
export function useAudio({ onChunk, sampleRate = 16000 }) {
    const [isCapturing, setIsCapturing] = useState(false);
    const [error, setError] = useState(null);
    const contextRef = useRef(null);
    const processorRef = useRef(null);
    const streamRef = useRef(null);
    const start = useCallback(async () => {
        try {
            setError(null);
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate,
                    echoCancellation: true,
                    noiseSuppression: true,
                },
            });
            const ctx = new AudioContext({ sampleRate });
            const source = ctx.createMediaStreamSource(stream);
            const processor = ctx.createScriptProcessor(4096, 1, 1);
            processor.onaudioprocess = (e) => {
                const float32 = e.inputBuffer.getChannelData(0);
                const pcm16 = new Int16Array(float32.length);
                for (let i = 0; i < float32.length; i++) {
                    pcm16[i] = Math.max(-32768, Math.min(32767, float32[i] * 32768));
                }
                onChunk(pcm16.buffer);
            };
            source.connect(processor);
            processor.connect(ctx.destination);
            contextRef.current = ctx;
            processorRef.current = processor;
            streamRef.current = stream;
            setIsCapturing(true);
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : "Microphone access denied";
            setError(msg);
            throw err;
        }
    }, [onChunk, sampleRate]);
    const stop = useCallback(() => {
        processorRef.current?.disconnect();
        contextRef.current?.close();
        streamRef.current?.getTracks().forEach((t) => t.stop());
        processorRef.current = null;
        contextRef.current = null;
        streamRef.current = null;
        setIsCapturing(false);
    }, []);
    return { start, stop, isCapturing, error };
}
